<template>
	<div>
		<v-dialog v-model="dialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition">
			<v-card>
				<v-toolbar dark color="primary">
					<v-btn icon dark @click.stop="closeDialog()">
						<v-icon>mdi-close</v-icon>
					</v-btn>
					<v-toolbar-title>Stallholder Details</v-toolbar-title>
					<div class="flex-grow-1"></div>
					<v-toolbar-items>
						<v-btn dark text @click.stop="submitForm()" :disabled="savingDialog" :loading="savingDialog">{{ formAction }}</v-btn>
					</v-toolbar-items>
				</v-toolbar>
				<v-alert v-model="alert.show" :dismissible="alert.dismissible" :type="alert.type" transition="fade-transition" class="ma-4">
					{{ alert.text }}
				</v-alert>
				<v-form ref="form" v-model="form" class="pa-4 pt-6">
					<v-radio-group v-model="formData.stallholder_type" row label="Stallholder type:">
						<v-radio v-for="(item, i) in getStallholderTypes" color="primary" :key="i" :label="item.label" :value="item.name"> </v-radio>
					</v-radio-group>
					<v-row>
						<v-col class="pt-0" cols="12" sm="6" lg="6">
							<v-text-field v-model="formData.stall_name" filled label="Business Name" counter="35" :rules="[rules.required, rules.maxLength(35, formData.stall_name)]"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="3">
							<v-text-field v-model="formData.first_name" filled label="First Name" :rules="[rules.required]"></v-text-field>
						</v-col>
						<v-col class="pt-0" sm="6" cols="12" lg="3">
							<v-text-field v-model="formData.last_name" filled label="Last Name" :rules="[rules.required]"></v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col class="pt-0" cols="12" sm="6" lg="6">
							<v-text-field v-model="formData.email" filled label="Email address" type="email" :rules="[rules.email]"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="3">
							<v-text-field v-model="formData.phone" filled label="Phone number"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="3">
							<v-text-field v-model="formData.vehicle_registration" filled label="Vehicle Reg."></v-text-field>
						</v-col>
					</v-row>
					<v-textarea v-model="formData.description" auto-grow filled label="Description" hint="Short description displayed on Market Map & booking form" persistent-hint rows="3" counter="125" :rules="[rules.maxLength(125, formData.description)]"></v-textarea>

					<v-select :items="$store.state.categories" label="Category" filled v-model="formData.category" item-value="id" item-text="name" :rules="[rules.required]"></v-select>

					<v-textarea v-model="formData.admin_note" auto-grow filled label="Admin Note" rows="3" counter="250" :rules="[rules.maxLength(250, formData.admin_note)]"></v-textarea>

					<v-subheader class="pl-0">Trade Permissions</v-subheader>
					<div class="d-flex">
						<v-checkbox v-for="perm in $store.state.permissionTypes" :key="perm.value" v-model="formData.trade_permissions" :label="perm.text" :value="perm.value" class="mr-4"></v-checkbox>
					</div>

					<div v-if="dialogVisible && formData.trade_permissions && (formData.trade_permissions.includes('food-eat') || formData.trade_permissions.includes('food-gourmet'))">
						<v-subheader class="pl-0">Food Certificate</v-subheader>
						<div v-if="formData.food_certificate" class="my-4">
							<a :href="formData.food_certificate.url" target="_blank">{{ formData.food_certificate.filename }}</a>
							<v-btn class="ml-2" icon @click="deleteFoodCertificate">
								<v-icon>mdi-delete</v-icon>
							</v-btn>
						</div>
						<v-file-input v-else chips label="Upload Food Certificate" v-model="foodCertificateFile" :rules="[rules.maxFileSize(2000000, foodCertificateFile)]"></v-file-input>
					</div>

					<v-divider></v-divider>
					<v-list-item class="pl-0 my-2">
						<v-list-item-content>
							<v-list-item-title class="headline">Profile</v-list-item-title>
							<v-list-item-subtitle>Profile page only available during market week if stallholder has paid for a listing </v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
					<v-textarea v-model="formData.profile_description" auto-grow filled label="Full Description" rows="3" counter="500" :rules="[rules.maxLength(500, formData.profile_description)]"></v-textarea>
					<v-row>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.profile_phone" filled label="Phone"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.profile_facebook_url" filled label="Facebook URL"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.profile_instagram_url" filled label="Instagram URL"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.profile_website_url" filled label="Website URL"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.profile_youtube_video_url" filled label="YouTube Video URL"></v-text-field>
						</v-col>
					</v-row>
					<uploads-grid :images="stallholderImages" v-on:delete:image="deleteImage($event)" />

					<v-file-input chips multiple label="Upload Images" v-model="files" accept="image/*" @change="onFileChange"></v-file-input>

					<v-list-item class="pl-0 my-2">
						<v-list-item-content>
							<v-list-item-title class="headline">Insurance Details</v-list-item-title>
						</v-list-item-content>
					</v-list-item>

					<v-row>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.insurance_company" filled label="Insurance Company"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.insured_name" filled label="Insured Name"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.insured_address" filled label="Insured Address"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.cover_from" filled label="Cover From"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.cover_to" filled label="Cover To"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.policy_number" filled label="Policy Number"></v-text-field>
						</v-col>
					</v-row>
				</v-form>
			</v-card>
		</v-dialog>

		<v-dialog v-model="savingDialog" persistent width="300">
			<v-card color="primary" dark>
				<v-card-text class="pt-5">
					<v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import UploadsGrid from './UploadsGrid.vue'

export default {
	data: () => ({
		dialog: '',
		formData: {},
		isSaveBtnLoading: false,
		isCloseBtnDisabled: false,
		isSaveBtnDisabled: false,
		savingDialog: false,
		alert: {
			show: false,
			text: '',
			type: 'success',
			dismissible: true,
		},
		form: false,
		formAction: 'create',
		rules: {
			password: (v) => (v || '').match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) || 'Password must contain an upper case letter, a numeric character, and a special character',
			required: (v) => !!v || 'This field is required',
			email: (v) => /.+@.+/.test(v) || 'Please enter a valid email',
			maxLength: (len, v) => (v || '').length <= len || `Invalid character length, maximum ${len}`,
			maxInt: (max, v) => (v || '') <= max || `Invalid number, maximum ${max}`,
			maxFileSize: (size, v) => !v || v.size < size || `Size should be less than ${size / 1000000}mb`,
		},
		alert: {
			show: false,
			text: '',
			type: 'info',
			dismissible: true,
		},
		initialStallholderFormData: {
			category: null,
			description: '',
			email: '',
			first_name: '',
			//has_account: false,
			id: 0,
			last_name: '',
			phone: '',
			admin_note: '',
			profile_description: '',
			profile_phone: '',
			profile_facebook_url: '',
			profile_images: [],
			profile_instagram_url: '',
			profile_website_url: '',
			profile_youtube_video_url: '',
			stall_name: '',
			stallholder_type: 'casual',
			trade_permissions: [],
			insurance_company: '',
			insured_name: '',
			insured_address: '',
			cover_from: '',
			cover_to: '',
			policy_number: '',
		},
		files: [],
		filesToUpload: [],
		filesToDelete: [],
		stallholderFilter: (item, queryText, itemText) => {
			let searchString = `${item.stall_name} ${item.first_name} ${item.last_name} ${item.email}`
			return searchString.toLowerCase().indexOf(queryText.toLowerCase()) > -1
		},
		foodCertificateFile: null,
	}),
	props: {
		dialogVisible: {
			type: Boolean,
			default: false,
			required: false,
		},
		selectedStallholder: {
			required: true,
		},
	},
	components: {
		UploadsGrid,
	},
	watch: {
		dialogVisible(newData, oldData) {
			if (newData) {
				console.log('dialog visible', this.selectedStallholder, this.formAction)
				console.log(newData, oldData)

				if (this.selectedStallholder && this.selectedStallholder.id) {
					this.formAction = 'update'
					this.formData = Object.assign({}, this.selectedStallholder)
				} else {
					if (this.$refs.form) this.$refs.form.resetValidation()
					this.formData = Object.assign({}, this.initialStallholderFormData)
				}
			}
		},
	},
	methods: {
		closeDialog() {
			this.filesToDelete = []
			this.filesToUpload = []

			this.$emit('close')
			// wait half a sec, before resetting data (otherwise we see it cleared before the dialog fully transitions)
			setTimeout(() => {
				this.isSaveBtnLoading = false
				this.formdata = {}
				this.resetAlert()
				this.resetForm()
			}, 500)
		},
		resetForm() {
			this.$refs.form.reset()
			this.formAction = 'create' //reset to default
		},
		submitForm() {
			this.resetAlert()

			if (this.$refs.form.validate()) this.saveStallholder()
		},
		saveStallholder() {
			this.savingDialog = true
			console.log('need to update DB here')
			let self = this

			let isUpdatingId = ''

			if (this.formData.id !== 0) isUpdatingId = this.formData.id

			var fdata = new FormData()

			for (const key in this.formData) {
				if (this.initialStallholderFormData.hasOwnProperty(key) && Array.isArray(this.initialStallholderFormData[key])) {
					if (!this.formData[key].length) fdata.append(key, '')
					else
						for (var i = 0; i < this.formData[key].length; i++) {
							fdata.append(`${key}[]`, this.formData[key][i])
						}
				} else if (key != 'profile_images') {
					// we don't POST profile_images only, profile_images_to_upload, profile_images_to_delete
					fdata.append(key, this.formData[key])
				}
			}

			for (var i = 0, len = this.filesToUpload.length; i < len; i++) {
				fdata.append('profile_images_to_upload[]', this.filesToUpload[i])
			}

			for (var i = 0, len = this.filesToDelete.length; i < len; i++) {
				fdata.append('profile_images_to_delete[]', this.filesToDelete[i])
			}

			console.warn(this.formData.food_certificate)
			console.warn(this.foodCertificateFile)

			if (this.foodCertificateFile) {
				fdata.set('food_certificate', this.foodCertificateFile) //changed or added file
			} else if (!this.formData.food_certificate) {
				fdata.set('food_certificate', '') // previous file delete by user, send empty so it is deleted
			} else {
				fdata.delete('food_certificate', '') // no changes made to food certifcate, so dont send field
			}

			axios({
				method: 'POST',
				url: `/wisdom-markets/v1/stallholders/${isUpdatingId}`,
				headers: {
					Authorization: `Bearer ${this.$store.state.accessToken}`,
					//'Content-Type': 'multipart/form-data'
				},
				data: fdata, //this.formData
			})
				.then((response) => {
					console.log('saved', response)
					console.warn(response)

					//this.closeDialog();
					this.savingDialog = false
					self.showAlert('success', 'Saved!')

					if (response.data.id && self.formAction == 'create') {
						console.log('adddding', response.data.id)
						//   this.$store.commit('addStallholder', response.data);
						this.$emit('create', response.data)
						this.files = []
						this.filesToUpload = []
						this.formData.id = response.data.id
						this.formData.profile_images = response.profile_images
						this.formAction = 'update'
						this.formData = Object.assign({}, response.data) // so we get new images in dialog otherwise not needed
					} else {
						//   this.$store.commit('updateStallholder', response.data);
						this.$emit('update', response.data)
						this.files = []
						this.filesToUpload = []
						this.filesToDelete = []
						console.log('reassisgning')
						this.formData = Object.assign({}, response.data) // so we get new images in dialog otherwise not needed
					}

					this.foodCertificateFile = null //reset incase just uploaded it, we dont want to reupload it, if they submit dialog again before closing it
				})
				.catch(function(error) {
					console.log('error saving', error)
					//todo display error popup
					// this.isSaveBtnLoading = false;
					// this.isCloseBtnDisabled = false;
					let message = 'Error!'

					if (error.response.data && error.response.data.message) message = message + ' ' + JSON.stringify(error.response.data.message)

					self.savingDialog = false
					self.showAlert('error', message)
				})
		},
		showAlert(type, text) {
			this.alert.show = true
			this.alert.text = text
			this.alert.type = type
		},
		resetAlert() {
			//and reset
			this.alert = {
				show: false,
				text: '',
				type: 'info',
				dismissible: true,
			}
		},
		onFileChange() {
			console.log('FILE CHANGE')

			if (this.files.length > 0) {
				this.files.forEach((f, index) => {
					console.log('select file type =' + f.type)
					console.log(f)
					if (!f.type.match('image.*') && !f.type.match('.*pdf') && !f.type.match('audio.*') && !f.type.match('.*wordprocessing')) {
						alert('Incorrect file format selected')
						return
					}
					if (f.size > 10485760) {
						alert('Maximum file size is 10mb')
						return
					}
					// if(this.files.length>3) {
					//     alert('Maximum of 3 files allowed');
					//     return;
					// }

					this.filesToUpload.push(f)
				})
			} else {
				this.filesToUpload = [] //clear files array
			}
		},
		deleteImage(image) {
			//this.deleteStallholderImages.push (image.id)
			this.filesToDelete.push(image.id)
			// let index = this.stallholderFormData.profile_images.findIndex(item => item.id===image.id);

			// if (index !== -1) {
			//   this.stallholderFormData.profile_images.splice(index, 1);
			//   this.stallholderFormData.profile_images_to_delete.push(image.id);
			// }
		},
		deleteFoodCertificate() {
			this.formData.food_certificate = ''
		},
	},
	computed: {
		//
		isDialogVisible() {
			if (this.dialog != this.dialogVisible) {
				return this.dialogVisible
			} else {
				return this.dialog
			}
		},
		...mapGetters(['getStallholderById', 'getStallholderTypes']),
		stallholderImages() {
			let data

			if (this.formData.profile_images) {
				data = this.formData.profile_images.filter((item) => {
					//if(this.selectedPaginationItem == item.id)
					//console.log()
					return !this.filesToDelete.some((deleteId) => {
						return item.id == deleteId
					})
				})
			}

			return data
		},
	},
	mounted() {},
}
</script>
